import { useMutation } from "@tanstack/react-query";
import { Button } from "@wags/ui/button.tsx";
import { authClient } from "@/lib/core/auth-client";

const SCOPE_LABELS: Record<string, string> = {
    openid: "Confirm it is you",
    offline_access: "Stay connected until you disconnect it",
    "wags:mcp": "Use Wags tools on your behalf",
};

export function oauthConsentScopes(search: string): string[] {
    return (new URLSearchParams(search).get("scope") ?? "")
        .split(" ")
        .filter((scope) => scope !== "");
}

export function OAuthConsentPage() {
    const scopes = oauthConsentScopes(window.location.search);
    const decision = useMutation({
        mutationFn: async (accept: boolean) => {
            const { data, error } = await authClient.oauth2.consent({ accept });
            if (error) throw new Error(error.message ?? "Consent failed");
            const target =
                (data as { redirect_uri?: string; url?: string } | null)
                    ?.redirect_uri ?? (data as { url?: string } | null)?.url;
            if (target) window.location.assign(target);
        },
    });
    return (
        <main className="mx-auto flex max-w-md flex-col gap-6 px-4 py-16 text-body">
            <h1 className="text-page-title">Connect an app to Wags</h1>
            <p>
                An app is asking to connect to your Wags account. It will be
                able to:
            </p>
            <ul className="list-disc pl-6">
                {scopes.map((scope) => (
                    <li key={scope}>{SCOPE_LABELS[scope] ?? scope}</li>
                ))}
            </ul>
            {decision.isError ? (
                <p role="alert">That didn't complete. Please try again.</p>
            ) : null}
            <div className="flex gap-3">
                <Button
                    disabled={decision.isPending}
                    onClick={() => decision.mutate(true)}
                >
                    Allow
                </Button>
                <Button
                    variant="outline"
                    disabled={decision.isPending}
                    onClick={() => decision.mutate(false)}
                >
                    Deny
                </Button>
            </div>
        </main>
    );
}

export const oauthConsentRoute = {
    ssr: false as const,
    head: () => ({ meta: [{ title: "Connect an app — Wags" }] }),
};
